import { ENTITLEMENTS } from 'studio5/constants/entitlements';
import {
  ThirdPartyIntegration,
  IntegrationDataFlow,
  IntegrationType,
  IntegrationVendor,
} from 'studio5/pages/integrations/integrations.types';

export const HUBSPOT_ID = 'hubspot-two-way-integration';
export const HUBSPOT_V2_ID = 'hubspot-integration-(code-native)';
export const MARKETO_ID = 'marketo';
export const SALESFORCE_ID = 'salesforce';
export const SALESFORCE_V2_ID = 'salesforce-integration-(code-native)';
export const SLACK_ID = 'slack-integration';
export const SLACK_V2_ID = 'slack-integration-(code-native)';
export const ZENDESK_SUPPORT_ID = 'zendesk-support-two-way-integration';

export type IntegrationThirdPartyId =
  | typeof HUBSPOT_ID
  | typeof HUBSPOT_V2_ID
  | typeof MARKETO_ID
  | typeof SALESFORCE_ID
  | typeof SALESFORCE_V2_ID
  | typeof SLACK_ID
  | typeof SLACK_V2_ID
  | typeof ZENDESK_SUPPORT_ID;

export const INTEGRATIONS_THIRD_PARTY: Record<
  IntegrationThirdPartyId,
  ThirdPartyIntegration
> = {
  [HUBSPOT_ID]: {
    id: HUBSPOT_ID,
    name: 'HubSpot',
    isPremium: true,
    entitlement: ENTITLEMENTS.HUBSPOT_ACCESS,
    supersededBy: HUBSPOT_V2_ID,
    logoAssetUrl: new URL('assets/hubspot.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/integration-documents/hubspot-integration?from_search=122469948',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Pass user properties for better targeting and enable teams to see how
        users are interacting with in-product experiences.
        <br />
        <br />
        Note: A new version of this integration, <strong>HubSpot (V2)</strong>,
        is now available. This new integration supports more Appcues events and
        improved reliability. To connect, first deactivate this integration and
        then activate the new integration. This legacy integration will be
        deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues' HubSpot integration makes it easy to target marketing campaigns based on how users interact with Appcues flows and set up flows, goals, and segments based on HubSpot Contact Record properties. It can also help your teams identify how individual contacts are interacting with in-product experiences.
      - Target and segment in-product experiences based on virtually any attribute in HubSpot—including plan tier, account owner, and lifecycle stage
      - Update HubSpot contacts in real-time as they interact with Appcues content—including NPS and survey responses, flow completion, and goals
      - Send emails to users who completed, skipped, or interacted with in-product experiences (such as emailing those who didn't complete an onboarding flow)
    `,
  },

  [HUBSPOT_V2_ID]: {
    id: HUBSPOT_V2_ID,
    name: 'HubSpot (V2)',
    isPremium: true,
    entitlement: ENTITLEMENTS.HUBSPOT_ACCESS,
    logoAssetUrl: new URL('assets/hubspot.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/integration-documents/hubspot-integration?from_search=122469948',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Pass user properties for better targeting and enable teams to see how
        users are interacting with in-product experiences.
        <br />
        <br />
        Note: This V2 HubSpot integration supports more Appcues events and
        improved reliability. If you are using the legacy integration, first
        deactivate it and then activate this new integration. The legacy HubSpot
        integration will be deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues' HubSpot integration makes it easy to target marketing campaigns based on how users interact with Appcues flows and set up flows, goals, and segments based on HubSpot Contact Record properties. It can also help your teams identify how individual contacts are interacting with in-product experiences.
      - Target and segment in-product experiences based on virtually any attribute in HubSpot—including plan tier, account owner, and lifecycle stage
      - Update HubSpot contacts in real-time as they interact with Appcues content—including NPS and survey responses, flow completion, and goals
      - Send emails to users who completed, skipped, or interacted with in-product experiences (such as emailing those who didn't complete an onboarding flow)
    `,
  },

  [MARKETO_ID]: {
    id: MARKETO_ID,
    name: 'Marketo',
    isPremium: true,
    entitlement: ENTITLEMENTS.MARKETO_ACCESS,
    logoAssetUrl: new URL('assets/marketo.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/marketo-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction. ',
    longDescription: `
      Appcues' Marketo Integration makes it easy to automatically target marketing campaigns based on user's interactions.
      - Send emails and other communications to users when they interact with Appcues experiences (user completes or skips  flows, checklists, etc.)
      - Follow up with users via email after they provide an in-product NPS score
      - Set up flows, goals, and segments based on Marketo Lead Record properties
      - Help your marketing team identify how individual contacts interact with in-product flows
    `,
  },

  [SALESFORCE_ID]: {
    id: SALESFORCE_ID,
    name: 'Salesforce',
    supersededBy: SALESFORCE_V2_ID,
    isPremium: true,
    entitlement: ENTITLEMENTS.SALESFORCE_ACCESS,
    logoAssetUrl: new URL('assets/salesforce.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/salesforce-two-way-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Pass user properties for better targeting and enable teams to see how
        users are interacting with in-product experiences.
        <br />
        <br />
        Note: A new version of this integration,{' '}
        <strong>Salesforce (V2)</strong>, is now available. This new integration
        supports more Appcues events and improved reliability. To connect, first
        deactivate this integration and then activate the new integration. This
        legacy integration will be deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues Salesforce Integration makes it easy to deliver more personalized and effective in-product experiences. It also enables teams to understand and see how users are interacting with in-product experiences.

      - Target and segment in-product experiences based on virtually any attribute in Salesforce—including plan tier, MRR amount, and lifecycle stage
      - Let sales and service teams see what prospects are doing in-product by updating leads and contacts in Salesforce when users skip, complete, and interact with in-product experiences (flows, checklists, etc.)
      - Automatically tag leads and contacts in Salesforce with NPS responses
      - Send Appcues survey and event data to Salesforce for automation and analysis
    `,
  },

  [SALESFORCE_V2_ID]: {
    id: SALESFORCE_V2_ID,
    name: 'Salesforce (V2)',
    isPremium: true,
    entitlement: ENTITLEMENTS.SALESFORCE_ACCESS,
    logoAssetUrl: new URL('assets/salesforce.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/salesforce-two-way-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Pass user properties for better targeting and enable teams to see how
        users are interacting with in-product experiences.
        <br />
        <br />
        Note: This V2 Salesforce integration supports more Appcues events and
        improved reliability. If you are using the legacy integration, first
        deactivate it and then activate this new integration. The legacy
        Salesforce integration will be deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues Salesforce Integration makes it easy to deliver more personalized and effective in-product experiences. It also enables teams to understand and see how users are interacting with in-product experiences.

      - Target and segment in-product experiences based on virtually any attribute in Salesforce—including plan tier, MRR amount, and lifecycle stage
      - Let sales and service teams see what prospects are doing in-product by updating leads and contacts in Salesforce when users skip, complete, and interact with in-product experiences (flows, checklists, etc.)
      - Automatically tag leads and contacts in Salesforce with NPS responses
      - Send Appcues survey and event data to Salesforce for automation and analysis
    `,
  },

  [SLACK_ID]: {
    id: SLACK_ID,
    name: 'Slack',
    isPremium: true,
    supersededBy: SLACK_V2_ID,
    entitlement: ENTITLEMENTS.SLACK_ACCESS,
    logoAssetUrl: new URL('assets/slack.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/slack-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Increase visibility and action with real time updates on user
        interactions right to your preferred channels.
        <br />
        <br />
        Note: A new version of this integration, <strong>Slack (V2)</strong>, is
        now available. This new integration supports more Appcues events and
        improved reliability. To connect, first deactivate this integration and
        then activate the new integration. This legacy integration will be
        deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues' Slack integration makes it easy to create a customer-centric organization by surfacing real-time user feedback and information in the places your teams are already in. Encourage teams to take action on what they see for immediate user follow-up.

      Automatically send details to a designated Slack channel, including:
      - NPS Scores and feedback
      - Form Submission content
      - Completed action (like a Flow or Checklist completion)
    `,
  },

  [SLACK_V2_ID]: {
    id: SLACK_V2_ID,
    name: 'Slack (V2)',
    isPremium: true,
    entitlement: ENTITLEMENTS.SLACK_ACCESS,
    logoAssetUrl: new URL('assets/slack.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/slack-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription: (
      <>
        Increase visibility and action with real time updates on user
        interactions right to your preferred channels.
        <br />
        <br />
        Note: This V2 Slack integration supports more Appcues events and
        improved reliability. If you are using the legacy integration, first
        deactivate it and then activate this new integration. The legacy Slack
        integration will be deprecated mid-2026.
      </>
    ),
    longDescription: `
      Appcues' Slack integration makes it easy to create a customer-centric organization by surfacing real-time user feedback and information in the places your teams are already in. Encourage teams to take action on what they see for immediate user follow-up.

      Automatically send details to a designated Slack channel, including:
      - NPS Scores and feedback
      - Form Submission content
      - Completed action (like a Flow or Checklist completion)
    `,
  },

  [ZENDESK_SUPPORT_ID]: {
    id: ZENDESK_SUPPORT_ID,
    name: 'Zendesk Support',
    isPremium: true,
    entitlement: ENTITLEMENTS.ZENDESK_ACCESS,
    logoAssetUrl: new URL('assets/zendesk-support.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/zendesk-support-integration',
    integrationType: IntegrationType.THIRD_PARTY_VENDOR,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    integrationVendor: IntegrationVendor.PRISMATIC,
    shortDescription:
      'Pass user and event data back and forth to personalize in-app experiences and inform support conversations',
    longDescription: `
      Appcues' Zendesk Support Integration makes it easy to show in-product interaction data to enable support teams in their conversations.

      It also allows for the creation of Appcues flows, goals, and segments based on Zendesk Sell Contact Record properties.
      - Send service and support data like CSAT and ticket volume from Zendesk to Appcues for better in-app targeting and personalization
      - Update the Zendesk Sell Contact Profile notes section after a user completes, skips, or interacts with an in-product experience (flow, checklist, etc.) to better inform support conversations
    `,
  },
} as const;
